import { Navigate, createBrowserRouter } from "react-router-dom";
import { AppMiddleware, OnboardingMiddleware } from "@/middleware";
import { AuthLayout, AppLayout, OnboardingLayout } from "@/layouts";
import { collectOnboardingRoutes } from "@/navigation/onboardingRouter";
import Login from "@/routes/Auth/Login";
import CompanySelector from "@/routes/Auth/CompanySelector";
import ResetPassword from "@/routes/Auth/ResetPassword";
import ForgotPassword from "@/routes/Auth/ForgotPassword";
import EmailVerify from "@/routes/Auth/EmailVerify";
import EmailVerifyExisting from "@/routes/Auth/EmailVerifyExisting";
import Signup from "@/routes/Onboarding/Collect/Signup";
import ComplySignUp from "@/routes/Onboarding/Comply/Signup";
import OnboardingHeader from "@/components/OnboardingHeader";
import OnboardingChecklist from "@/routes/Onboarding/OnboardingChecklist";
import MarketableAudiences from "@/routes/Common/MarketableAudiences";
import AuditLogs from "@/routes/Common/AuditLogs";
import ComplianceReportController from "@/routes/Common/ComplianceReport";
import ComplianceReportsView from "@/routes/Common/ComplianceReports";
import ContactDetailsController from "@/routes/Common/AuditLogs/ContactDetails";
import UsageDashboard from "@/routes/Email/UsageDashboard";
import WidgetController from "@/routes/Settings/Widget";
import Preview from "@/routes/Settings/Widget/Preview";
import HealthCheck from "@/routes/Sms/HealthCheck";
import HealthCheckHistory from "@/routes/Sms/HealthCheckHistory";
import ShopifyHealthHistory from "@/routes/Sms/HealthCheckHistory/ShopifyHealthHistory";
import KlaviyoHealthHistory from "@/routes/Sms/HealthCheckHistory/KlaviyoHealthHistory";
import SmsOnboardingPolicyContent from "@/routes/Sms/SmsOnboardingPolicyContent";
import { COLLECT_HOME_URL } from "@/utils/const";
import IntegrationController from "@/routes/Settings/Integration";
import KlaviyoLists from "@/routes/Settings/Integration/KlaviyoLists";
import EmailDashboard from "@/routes/Email/Dashboard";
import ShopifyApp from "@/routes/Settings/Integration/ShopifyApp";
import PrivacyCenterController from "@/routes/Comply/PrivacyCenter";
import LivePreview from "@/routes/Comply/PrivacyCenter/LivePreview";
import Language from "@/routes/Comply/PrivacyCenter/Language";
import WebsiteInstallation from "@/routes/Comply/PrivacyCenter/WebsiteInstallation";
import Theme from "@/routes/Comply/PrivacyCenter/Theme";
import UserManagement from "@/routes/Settings/UserManagement";
import ManageUser from "@/routes/Settings/UserManagement/Manage";
import Policies from "@/routes/Comply/Policies";
import ManagePolicy from "@/routes/Comply/Policies/Manage";
import CannotInstall from "@/routes/Public/CannotInstall";
import Support from "@/routes/Support";
import Billing from "@/routes/Settings/Billing";
import KlaviyoApiKey from "@/routes/Settings/Integration/KlaviyoApiKey";
import EmailInstallation from "@/routes/Settings/Widget/EmailInstallation";
import PrivacyDetails from "@/routes/Settings/Widget/PrivacyDetails";
import CompanyProfile from "@/routes/Settings/CompanyProfile";
import ShopSettingsController from "@/routes/Integrations/ShopSettings";
import MatListing from "@/routes/Integrations/MatSettings/MatListing";
import MatAuthentication from "@/routes/Integrations/MatSettings/MatAuthentication";
import MatConfiguration from "@/routes/Integrations/MatSettings/MatConfiguration";
import DisableCheckout from "@/routes/Integrations/ShopSettings/DisableCheckout";
import ChecklistMiddleware from "@/middleware/ChecklistMiddleware";

export const router = createBrowserRouter([
  {
    path: "marketing",
    element: (
      <AppMiddleware>
        <AppLayout />
      </AppMiddleware>
    ),
    children: [
      {
        path: "dashboard",
        element: <EmailDashboard />,
      },
      {
        path: "usage",
        element: <UsageDashboard />,
      },
      {
        path: "marketable-audiences",
        element: <MarketableAudiences channel="EMAIL" />,
      },
      {
        path: "audit-logs",
        children: [
          {
            index: true,
            element: <AuditLogs channel="EMAIL" />,
          },
          {
            path: ":id",
            children: [
              {
                index: true,
                element: <ContactDetailsController channel="EMAIL" />,
              },
              {
                path: "history/:contactHistoryId",
                element: <ContactDetailsController channel="EMAIL" />,
              },
            ],
          },
        ],
      },
      {
        path: "compliance-reports",
        children: [
          {
            index: true,
            element: <ComplianceReportsView />,
          },
          {
            path: ":id",
            element: <ComplianceReportController />,
          },
        ],
      },
    ],
  },
  {
    path: "sms",
    element: (
      <AppMiddleware>
        <AppLayout />
      </AppMiddleware>
    ),
    children: [
      {
        path: "health-check",
        element: <HealthCheck />,
      },
      {
        path: "health-check-history",
        element: <HealthCheckHistory />,
        children: [
          {
            index: true,
            element: <ShopifyHealthHistory />,
          },
          {
            path: "shopify",
            element: <ShopifyHealthHistory />,
          },
          {
            path: "klaviyo",
            element: <KlaviyoHealthHistory />,
          },
        ],
      },
      {
        path: "marketable-audiences",
        element: <MarketableAudiences channel="SMS" />,
      },
      {
        path: "audit-logs",
        children: [
          {
            index: true,
            element: <AuditLogs channel="SMS" />,
          },
          {
            path: ":id",
            children: [
              {
                index: true,
                element: <ContactDetailsController channel="SMS" />,
              },
              {
                path: "history/:contactHistoryId",
                element: <ContactDetailsController channel="SMS" />,
              },
            ],
          },
        ],
      },
      {
        path: "compliance-reports",
        children: [
          {
            index: true,
            element: <ComplianceReportsView />,
          },
          {
            path: ":id",
            element: <ComplianceReportController />,
          },
        ],
      },
    ],
  },
  {
    path: "settings",
    element: (
      <AppMiddleware>
        <AppLayout />
      </AppMiddleware>
    ),
    children: [
      {
        path: "widget",
        element: <WidgetController />,
        children: [
          { index: true, element: <Preview /> },
          { path: "preview", element: <Preview /> },
          { path: "location", element: <div>Widget Settings Location</div> },
          {
            path: "privacy-details",
            element: <PrivacyDetails />,
          },
          {
            path: "email-installation",
            element: <EmailInstallation />,
          },
          {
            path: "sms-installation",
            element: <div>Widget Settings SMS Installation</div>,
          },
        ],
      },
      {
        path: "integration",
        element: <IntegrationController />,
        children: [
          { index: true, element: <KlaviyoApiKey /> },
          {
            path: "klaviyo-api-key",
            element: <KlaviyoApiKey />,
          },
          {
            path: "klaviyo-phone-numbers",
            element: <div>Klaviyo Phone Numbers</div>,
          },
          {
            path: "klaviyo-lists",
            element: <KlaviyoLists />,
          },
          {
            path: "shopify-app",
            element: <ShopifyApp />,
          },
        ],
      },
      {
        path: "company-profile",
        element: <CompanyProfile />,
      },
      {
        path: "billing",
        element: <Billing />,
      },
      {
        path: "user-management",
        children: [
          {
            index: true,
            element: <UserManagement />,
          },
          {
            path: "add",
            element: <ManageUser type="add" />,
          },
          {
            path: "edit",
            element: <ManageUser type="edit" />,
          },
        ],
      },
    ],
  },
  {
    path: "profile",
    element: (
      <AppMiddleware>
        <AppLayout />
      </AppMiddleware>
    ),
    children: [
      {
        path: "company",
        element: <CompanyProfile />,
      },
      {
        path: "billing",
        element: <Billing />,
      },
      {
        path: "user-management",

        children: [
          {
            index: true,
            element: <UserManagement />,
          },
          {
            path: "add",
            element: <ManageUser type="add" />,
          },
          {
            path: "edit",
            element: <ManageUser type="edit" />,
          },
        ],
      },
    ],
  },
  {
    path: "integrations",
    element: (
      <AppMiddleware>
        <AppLayout />
      </AppMiddleware>
    ),
    children: [
      {
        path: "mat-settings",
        children: [
          {
            index: true,
            element: <MatListing />,
          },
          {
            path: ":integration",
            children: [
              {
                path: "authentication",
                element: <MatAuthentication />,
              },
              {
                path: "configuration",
                element: <MatConfiguration />,
              },
            ],
          },
        ],
      },
      {
        path: "shop-settings",
        element: <ShopSettingsController />,
        children: [
          {
            index: true,
            element: <PrivacyDetails />,
          },
          {
            path: "privacy-details",
            element: <PrivacyDetails />,
          },
          {
            path: "disable-checkout",
            element: <DisableCheckout />,
          },
          {
            path: "installation",
            element: <EmailInstallation />,
          },
          { path: "preview", element: <Preview /> },
        ],
      },
    ],
  },
  {
    path: "comply",
    element: (
      <AppMiddleware>
        <AppLayout />
      </AppMiddleware>
    ),
    children: [
      {
        path: "privacy-centre",
        element: <PrivacyCenterController />,
        children: [
          { path: "theme", element: <Theme /> },
          { path: "language", element: <Language /> },
          { path: "preview", element: <LivePreview /> },
          { path: "website-installation", element: <WebsiteInstallation /> },
        ],
      },
      {
        path: "policies",
        children: [
          {
            index: true,
            element: <Policies />,
          },
          {
            path: "edit",
            children: [
              {
                path: "text/:id",
                element: <ManagePolicy updateType="EDIT" policyType="TEXT" />,
              },
              {
                path: "pdf/:id",
                element: <ManagePolicy updateType="EDIT" policyType="PDF" />,
              },
            ],
          },
          {
            path: "add",
            children: [
              {
                path: "text",
                element: <ManagePolicy updateType="ADD" policyType="TEXT" />,
              },
              {
                path: "pdf",
                element: <ManagePolicy updateType="ADD" policyType="PDF" />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "support",
    element: (
      <AppMiddleware>
        <AppLayout />
      </AppMiddleware>
    ),
    children: [
      {
        index: true,
        element: <Support />,
      },
    ],
  },
  {
    path: "/help/support",
    element: (
      <AppMiddleware>
        <AppLayout />
      </AppMiddleware>
    ),
    children: [
      {
        index: true,
        element: <Support />,
      },
    ],
  },
  {
    path: "/auth",
    element: (
      <AppMiddleware protection="unprotected">
        <AuthLayout />
      </AppMiddleware>
    ),
    children: [
      {
        index: true,
        element: <Login />,
      },
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
    ],
  },
  {
    path: "/auth/company-selector",
    element: (
      <AppMiddleware protection="protected">
        <AuthLayout />
      </AppMiddleware>
    ),
    children: [
      {
        index: true,
        element: <CompanySelector />,
      },
    ],
  },
  {
    path: "/auth/email-verify",
    element: (
      <AppMiddleware protection="unprotected">
        <EmailVerify />
      </AppMiddleware>
    ),
  },
  {
    path: "/auth/email-verify-existing",
    element: (
      <AppMiddleware protection="unprotected">
        <EmailVerifyExisting />
      </AppMiddleware>
    ),
  },
  {
    path: "/onboarding/comply/signup",
    element: (
      <AppMiddleware protection="unprotected">
        <OnboardingHeader />
        <ComplySignUp />
      </AppMiddleware>
    ),
  },
  {
    path: "/onboarding/marketing/signup",
    element: (
      <AppMiddleware protection="unprotected">
        <OnboardingHeader />
        <Signup />
      </AppMiddleware>
    ),
  },
  {
    path: "/integrations/checklist",
    element: (
      <AppMiddleware protection="protected">
        <ChecklistMiddleware>
          <AppLayout />
        </ChecklistMiddleware>
      </AppMiddleware>
    ),
    children: [
      {
        index: true,
        element: <OnboardingChecklist />,
      },
    ],
  },
  {
    path: "/onboarding/marketing/*",
    element: (
      <AppMiddleware protection="protected">
        <OnboardingMiddleware>
          <OnboardingLayout />
        </OnboardingMiddleware>
      </AppMiddleware>
    ),
    children: collectOnboardingRoutes,
  },
  {
    path: "/sms-onboarding/:token",
    element: <SmsOnboardingPolicyContent />,
  },
  {
    path: "/integration/cannot-install",
    element: <CannotInstall />,
  },
  {
    path: "*",
    element: <Navigate to={COLLECT_HOME_URL} replace />,
  },
]);
